// kfe-pathway.js — squiggle pathway screen, level-aware.
//
// PORT TARGET: Assets/KFE/Scripts/Runtime/Core/UI/Pathway/PathwayScreen.cs
//
// Two routes share this component, parameterised by `level`:
//   /                  → level="stage"     (orange path, stages of the course)
//   /stage/<stageId>   → level="lesson"    (cyan path, lessons inside that stage)
//
// Tap on a stage navigates to /stage/<stageId>; tap on a lesson navigates
// to /lesson/<lessonId>. The squiggle component itself is dumb about the
// level — it just renders what we hand it. The level-specific stroke
// colour is applied via the squiggle's `level` prop.
//
// Brief 022: data comes from the pathway loader (`/content/kfe_pathways.json`
// + per-pathway tree json). Gates inline in the data are filtered out of
// the rendered nodes but still participate in locking via
// `pathwayFromOrderedNodes`. Multi-pathway switching shipped in Brief 157: the
// active pathway is read from getActivePathwayId() (localStorage `kfe_pathway_id`,
// default ZTF) and this screen reloads on the `kfe:pathway-changed` event.
//
// Brief 032: intro lessons (`isIntro: true`) are stripped from the squiggle
// (so numbering stays "node N = Lesson N") and surfaced as a separate row
// list above the squiggle. Each row is tappable and navigates to the same
// `/lesson/<id>` route as a numbered lesson. Locked intros (gateRule)
// render at reduced opacity and don't navigate. iOS canon: stage-overview
// affordance in LessonChartViewController.m / sibling stage screens.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t, getLanguage } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { pathwayFromOrderedNodes, deriveStageComplete, deriveLessonComplete } from "../data/pathway-builder.js";
import { loadPathway, loadLesson } from "../data/pathway-loader.js";
import { resolveRequiredPlay } from "../data/item-completion.js";
import {
  buildSingleFolderTree,
  getInaccessibleItems,
} from "../data/gating.js";
import { isGatingExemptId } from "../data/gating-config.js";
import { getActivePathwayId } from "../core/active-pathway.js";
import { navigate } from "../router.js";

/**
 * One stage-intro row — the web's KFEGuideView (Brief 198 item 6).
 *
 * `titleEn` / `titleZh` come from the pathway tree, so the row can paint on
 * first paint with no lesson fetch. Everything below them comes from the intro
 * LESSON's single item and the learner store, filled in by
 * `_scheduleIntroDetailRefresh` just after first paint (same paint-first
 * posture as the Brief 163 roll-up).
 *
 * @typedef {object} IntroRow
 * @property {string|number} id
 * @property {string} titleEn
 * @property {string} [titleZh]
 * @property {import("../data/gating.js").GateRule} [gateRule]
 * @property {string} [descEn]        item.descEn — iOS introModel.descriptionEn
 * @property {string} [descZh]        item.descZh — iOS introModel.descriptionZh
 * @property {"html"|"audio"|"video"|"audioHtml"} [iconKind]
 * @property {number} [requiredPlay]  < 1 hides the play ring, as iOS does
 * @property {number} [completedCount]
 */

/**
 * One squiggle node plus its roll-up derivation (Brief 163, Fix 1 / Option A).
 * `node` is the ordered node WITHOUT a `derivedComplete` decision — that is
 * supplied either from the persisted cache (first paint) or by running `derive`
 * in the background. `derive` is null for gate / gating-exempt nodes, which are
 * never required children of any roll-up and read their state the old way.
 *
 * @typedef {object} RollupEntry
 * @property {string|number} id
 * @property {import("../data/pathway-builder.js").OrderedPathwayNode} node
 * @property {(() => Promise<boolean>) | null} derive
 */

export class KFEPathway extends KFEComponent {
  static props = ["level", "stageId"];
  static styles = `
    :host { display: block; }
    .empty { color: var(--kfe-muted); padding: 2rem; text-align: center; }

    /* Stage intro row — the web's KFEGuideView (Brief 198 item 6). Every
       number below is from
       iEnglish/Classes/CustomViews/KFEGuideView/KFEGuideView.xib on
       KFE-4.0-App @ origin/new_KFEframework: a 50 pt full-width row, a 26 pt
       icon inset 10, text from x=46, a 35 pt play ring inset 25 from the
       trailing edge, a chevron inset 10, and a 0.5 pt hairline underneath. It
       replaces Brief 032's card with an OVERVIEW badge, which was invented. */
    .intros {
      list-style: none;
      padding: 0;
      margin: 0 0 0.25rem;
      /* Full-bleed and flush under the nav bar, like iOS's guideView (it is
         the first thing in the view, at y=0 — StagesViewController.m:451). */
      margin-inline: calc(-1 * var(--kfe-page-pad, 1rem));
      margin-top: calc(-1 * var(--kfe-page-pad, 1rem));
    }
    .guide {
      position: relative;
      display: flex;
      align-items: center;
      height: 50px;
      box-sizing: border-box;
      padding-inline: 10px;
      gap: 10px;
      cursor: pointer;
      color: var(--kfe-fg);
      font-family: var(--kfe-font);
      /* KFEGuideView's resting background is colorWithWhite:0.2
         (StagesViewController.m:230 / LessonsViewController.m). */
      background: var(--kfe-guide-bg, #333333);
    }
    /* KFEGuideView.xib EHI-Rs-DEe — a 0.5 pt rule in RGB(0.298) along the
       bottom edge. */
    .guide::after {
      content: "";
      position: absolute;
      left: 0; right: 0; bottom: 0;
      height: 0.5px;
      background: #4c4c4c;
    }
    /* StagesViewController.m:226-231 — while the intro is unplayed iOS pulses
       the row between white 0.2 and white 0.5 on a 2 s autoreversing repeat. */
    .guide.pulsing { animation: kfe-guide-pulse 2s ease-in-out infinite alternate; }
    @keyframes kfe-guide-pulse {
      from { background: #333333; }
      to   { background: #808080; }
    }
    @media (prefers-reduced-motion: reduce) {
      .guide.pulsing { animation: none; background: #4d4d4d; }
    }
    .guide:focus-visible {
      outline: 2px solid var(--kfe-line-lesson, #29abe2);
      outline-offset: -2px;
    }
    .guide.locked { opacity: var(--kfe-locked-opacity); cursor: not-allowed; }

    .guide-icon { flex: 0 0 auto; width: 26px; height: 26px; display: block; }
    .guide-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    /* IdealSans-Semibold 13 / IdealSans-Light 12, both white (xib a3j / XdH). */
    .guide-headline {
      font-size: 13px; font-weight: 600; color: #fff; line-height: 15px;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .guide-subhead {
      font-size: 12px; font-weight: 300; color: #fff; line-height: 14px;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    /* The play ring: a 35 pt box inset 25 from the trailing edge, with the
       count centred on it at system 10 pt (xib GFU / gIb). */
    .guide-plays {
      flex: 0 0 auto;
      position: relative;
      width: 35px; height: 35px;
      margin-right: 15px; /* 25 trailing − the row's own 10 padding */
      display: grid; place-items: center;
    }
    .guide-plays svg { width: 31px; height: 35px; display: block; }
    .guide-count {
      position: absolute;
      font-size: 10px; color: #fff; line-height: 1;
      transform: translateY(2px); /* xib 7uU-b6-uKK centreY + 2 */
    }
    /* RightArr, 7.5 × 13 — orange in the shipped build (see the reference
       screenshot), the same tint as the nav-bar chevron. */
    .guide-chevron {
      flex: 0 0 auto;
      color: var(--kfe-accent);
      font-size: 22px; line-height: 1;
      font-family: var(--kfe-font);
    }

    /* Horizontal scroll-snap pages — no JS gesture handling.

       Brief 198: bleed out of the shell's page gutter so each page is the
       full screen width. iOS sizes the squiggle from the whole screen
       (StagesViewController.m:451 — the gating-line view's frame is
       CGRectGetWidth(self.view.bounds) wide) and insets the drawing box
       itself by 35 pt (:457, glv.frame.size.width - 70). Leaving the
       1 rem gutter on top of that shrank every node by ~9%. */
    .pages {
      margin-inline: calc(-1 * var(--kfe-page-pad, 1rem));
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: 100%;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;
    }
    .pages::-webkit-scrollbar { display: none; }
    .pages > kfe-squiggle {
      scroll-snap-align: start;
      padding-block: 0.5rem;
      justify-self: center;
    }

    /* The caption under the squiggle — StagesViewController.m:204
       bottomHintLabel, KFELocalized("Tap Stage number to access stage") /
       LessonsViewController's lesson wording (Brief 198 item 7). */
    p.hint {
      margin: 0.25rem 0 0;
      text-align: center;
      color: #fff;
      font-family: var(--kfe-font);
      font-size: 15px;
      font-weight: 300;
    }

    /* Page indicator dots — a UIPageControl. Driven by IntersectionObserver
       below. Brief 198 item 8: iOS shows it even when there is ONE page (see
       docs/198-reference/ios-lesson-map.jpg), so it is no longer conditional
       on pages.length > 1. */
    nav.page-dots {
      display: flex;
      justify-content: center;
      gap: 9px;
      margin-top: 0.75rem;
    }
    nav.page-dots span {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #808080;
    }
    nav.page-dots span.active { background: #fff; }
  `;

  /** @type {import("../data/pathway-builder.js").PathwayPage[]} */
  _pages = [];
  /** @type {IntersectionObserver | null} */
  _observer = null;
  _activePage = 0;
  /** @type {"stage" | "lesson"} */
  _level = "stage";
  _gatingEnabled = true;
  /** @type {IntroRow[]} */
  _intros = [];
  /** @type {Set<string>} */
  _introLocked = new Set();
  /** False until onConnected has finished loading the pathway. Distinguishes
   *  the initial load (show "Loading…") from a genuinely empty pathway (show
   *  the empty/diagnostic message). Brief 122, Symptom A. */
  _loaded = false;
  /** Brief 168 dispatch fork: set to the pathway id when the active pathway is a
   *  flat (menu) shape — no `stages`, only `items`. render() then delegates to
   *  <kfe-pathway-list> instead of the squiggle. Null for squiggle pathways. */
  /** @type {string | null} */
  _flatPathwayId = null;

  async onConnected() {
    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    // Brief 157: re-load against the new pathway when the learner switches study
    // paths while this screen is mounted. The primary switch flow navigates to
    // "/" (a fresh mount that reads getActivePathwayId on connect); this handler
    // covers the both-levels mounted-screen case. addCleanup per the
    // event-handler-stacking gotcha (CLAUDE.md Common Pitfall #1).
    const onPathwayChanged = () => { void this._loadActivePathway(); };
    document.addEventListener("kfe:pathway-changed", onPathwayChanged);
    this.addCleanup(() => document.removeEventListener("kfe:pathway-changed", onPathwayChanged));

    await this._loadActivePathway();
  }

  /**
   * Load the active pathway (Brief 157: localStorage-backed getActivePathwayId)
   * and paint the squiggle for this screen's level. Re-runnable: invoked on
   * connect and again on `kfe:pathway-changed`.
   */
  async _loadActivePathway() {
    const level = /** @type {any} */ (this).level === "lesson" ? "lesson" : "stage";
    const stageId = /** @type {string|undefined} */ (/** @type {any} */ (this).stageId);

    const pathwayId = getActivePathwayId();

    let headerTitle = t("pathway.title");
    /** @type {RollupEntry[]} */
    let entries = [];
    /** @type {IntroRow[]} */
    let intros = [];
    /** @type {Set<string>} */
    let introLocked = new Set();

    try {
      const pathway = await loadPathway(pathwayId);
      headerTitle = pathway.titleEn || headerTitle;

      // Brief 168 dispatch fork: iOS picks the home VC by pathway shape —
      // StagesViewController (squiggle) vs TableListViewController (flat menu).
      // A flat pathway has no `stages`, only `items`; render the list mirror
      // (<kfe-pathway-list>) instead of the squiggle. The web router mounts one
      // tag per route, so kfe-pathway delegates here rather than introducing a
      // third dispatcher component at "/". Only at stage level: flat pathways
      // have no stages, so /stage/:id is never reached for them.
      if (
        level === "stage" &&
        !(pathway.stages && pathway.stages.length) &&
        Array.isArray(/** @type {any} */ (pathway).items) &&
        /** @type {any} */ (pathway).items.length
      ) {
        this._flatPathwayId = pathwayId;
        this._intros = [];
        this._level = level;
        this._loaded = true;
        this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
          detail: { title: headerTitle },
          bubbles: true,
          composed: true,
        }));
        this._scheduleRender();
        return;
      }
      this._flatPathwayId = null;

      if (level === "stage") {
        // Brief 151/153: a stage's "completed" signal is DERIVED from its
        // lessons' per-item progress (Option A — no persisted roll-up ROW).
        // Without it every stage reads completedCount 0, so no "End Stage N
        // Gate" can open and Stage 3+ stay sealed.
        //
        // Brief 163: that derive must NOT gate first paint. Each
        // deriveStageComplete fetches every lesson JSON in the stage — on a cold
        // cache that is a 50+ request fetch storm rooted right here (the brief's
        // DevTools call stack: kfe-pathway → deriveStageComplete →
        // deriveLessonComplete → loadLesson). So we no longer await the derive
        // before paint. Each stage becomes a RollupEntry whose `derive` is run by
        // _buildAndPaint: the first paint uses the PERSISTED roll-up cache (no
        // fetch); stale/missing entries recompute in the background and re-render.
        // Gate / exempt stages aren't required children of any roll-up → no derive.
        entries = (pathway.stages || []).map((s) => ({
          id: s.id,
          node: stageEntryToNode(s),
          derive: (s.type !== "gate" && !isGatingExemptId(s.id, /** @type {any} */ (s).excludeFromGating))
            ? () => deriveStageComplete(pathwayId, s)
            : null,
        }));
      } else if (stageId) {
        const stage = (pathway.stages || []).find(
          /** @param {import("../data/pathway-loader.js").PathwayStage} s */
          (s) => s.id === stageId,
        );
        if (stage) {
          headerTitle = stage.titleEn || t("pathway.stageTitle", { id: String(stage.order ?? "?") });
          const lessons = stage.lessons || [];
          // Brief 032: intros surface in a separate row list above the
          // squiggle. pathwayFromOrderedNodes already drops them from the
          // numbered nodes via isIntroItem; here we capture them in pathway
          // order so the affordance can render them.
          intros = lessons
            .filter((l) => l && l.isIntro === true && l.type !== "gate")
            .map((l) => ({
              id: l.id,
              titleEn: l.titleEn ?? "",
              titleZh: l.titleZh,
              gateRule: /** @type {any} */ (l.gateRule),
            }));
          // Brief 153: per-lesson completion derives the same way Brief 151
          // derives per-stage completion — render the iOS-canonical "completed"
          // green on the cyan squiggle once every visible required item is done.
          //
          // iOS canon (read per Rule #9 — see also kfe-squiggle.js' node-state
          // comment): `LessonsViewController.m` runs `redrawSquiggles` (locked →
          // grey, accessible → white) then `updateSquigglesCompletedItemsToGreen`,
          // which OVERRIDES every `completed==1` node to green #8CC63F — a
          // learner-progress signal carrying NO gating check, so it derives
          // REGARDLESS of gatingEnabled.
          //
          // Brief 163: same paint-first treatment as the stage branch — cached on
          // first paint, recomputed in the background (no `lesson_*.json` fetch on
          // the render path). The exempt guard is a defensive no-op today (only
          // stage_143095 is exempt) kept for future per-lesson flags.
          entries = lessons.map((l) => ({
            id: l.id,
            node: lessonEntryToNode(l),
            derive: (l.type !== "gate" && !isGatingExemptId(l.id, /** @type {any} */ (l).excludeFromGating))
              ? () => deriveLessonComplete(pathwayId, l)
              : null,
          }));

          // Locked-state for intros: reuse the gating module against the
          // stage's whole lesson list so any gateRule on an intro is
          // honoured. Operates on the already-loaded pathway tree — no lesson
          // fetch, so it stays on the first-paint path.
          const gatingEnabled = (await services.learner.getSetting("gating_enabled")) !== false;
          if (gatingEnabled && intros.length > 0) {
            /** @type {import("../data/gating.js").ItemNode[]} */
            const nodes = lessons.map((l) => ({
              id: l.id,
              isGate: l.type === "gate",
              isIntroItem: l.isIntro === true,
              isGatingExempt: isGatingExemptId(l.id, /** @type {any} */ (l).excludeFromGating),
              gateRule: /** @type {any} */ (l.gateRule),
            }));
            const folderId = "__stage_" + String(stage.id);
            const tree = buildSingleFolderTree(folderId, nodes);
            const ids = getInaccessibleItems(folderId, {
              gatingEnabled: true,
              completedCounts: {},
            }, tree);
            introLocked = new Set(ids.map(String));
          }
        } else {
          console.warn(`[pathway] stage not found in ${pathwayId}: ${stageId}`);
        }
      }
    } catch (e) {
      console.warn("[pathway] failed to load pathway", e);
    }
    this._intros = intros;
    this._introLocked = introLocked;
    this._level = level;
    this._gatingEnabled = (await services.learner.getSetting("gating_enabled")) !== false;

    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title: headerTitle },
      bubbles: true,
      composed: true,
    }));

    // Brief 163: paint NOW from the persisted roll-up cache (zero lesson
    // fetches), then recompute stale entries off the critical path.
    await this._buildAndPaint(pathwayId, entries);
  }

  /**
   * Brief 163 (Fix 1 / Option A) — paint-first roll-up. Builds the squiggle
   * nodes from the PERSISTED roll-up cache so first paint awaits ZERO
   * `lesson_*.json` fetches (the cold-cache regression's root cause — see the
   * brief's call stack), paints, then recomputes any stale or missing entries in
   * the background and re-renders only if a completion result actually moved.
   *
   * @param {string} pathwayId
   * @param {RollupEntry[]} entries
   */
  async _buildAndPaint(pathwayId, entries) {
    const learner = /** @type {any} */ (services.learner);
    /** @type {Map<string, {id:string, tick:number, complete:boolean}>} */
    let cache = new Map();
    try {
      const rows = await learner.getAllRollupCache();
      cache = new Map(rows.map((/** @type {any} */ r) => [String(r.id), r]));
    } catch (e) {
      console.warn("[pathway] roll-up cache read failed; first paint assumes incomplete", e);
    }

    // Seed each derivable node's completion from cache (or false when no cache
    // entry yet). Gate / exempt nodes keep derivedComplete undefined → the
    // existing store-read path in pathwayFromOrderedNodes (unchanged).
    for (const e of entries) {
      if (!e.derive) continue;
      const c = cache.get(String(e.id));
      e.node.derivedComplete = c ? c.complete : false;
    }

    try {
      this._pages = await pathwayFromOrderedNodes(entries.map((e) => e.node));
    } catch (e) {
      console.warn("[pathway] failed to compute pathway pages", e);
      this._pages = [];
    }
    this._loaded = true;
    this._scheduleRender();
    queueMicrotask(() => this._observePages());

    this._scheduleRollupRefresh(pathwayId, entries, cache);
    this._scheduleIntroDetailRefresh(pathwayId);
  }

  /**
   * Recompute stale / missing roll-up entries off the critical path, persist
   * them keyed by the current progress tick, and re-render only if a node's
   * completion changed from what first paint showed. Scheduled on idle so it
   * never contends with the user's first interaction or the deferred content
   * prefetch (Brief 163, Fix 2). Conservative on failure: a derive that throws
   * (e.g. a lesson JSON that can't load) counts as NOT complete — never
   * over-unlocks, mirroring deriveLessonComplete.
   *
   * @param {string} pathwayId
   * @param {RollupEntry[]} entries
   * @param {Map<string, {id:string, tick:number, complete:boolean}>} cache entries used for first paint
   */
  _scheduleRollupRefresh(pathwayId, entries, cache) {
    const learner = /** @type {any} */ (services.learner);
    const run = async () => {
      let currentTick = 0;
      try { currentTick = await learner.getProgressTick(); } catch { currentTick = 0; }
      let changed = false;
      for (const e of entries) {
        if (!e.derive) continue;
        if (!this._connected) return;
        const cached = cache.get(String(e.id));
        if (cached && cached.tick === currentTick) continue; // fresh — no fetch needed
        let complete = false;
        try { complete = await e.derive(); } catch { complete = false; }
        try { await learner.setRollupCacheEntry(e.id, currentTick, complete); } catch { /* cache is best-effort */ }
        const painted = cached ? cached.complete : false;
        if (complete !== painted) changed = true;
        e.node.derivedComplete = complete;
      }
      if (changed && this._connected) {
        try {
          this._pages = await pathwayFromOrderedNodes(entries.map((e) => e.node));
          this._scheduleRender();
          queueMicrotask(() => this._observePages());
        } catch (err) {
          console.warn("[pathway] roll-up refresh re-render failed", err);
        }
      }
    };
    const schedule = (typeof requestIdleCallback === "function")
      ? requestIdleCallback
      : /** @param {() => void} cb */ (cb) => setTimeout(cb, 0);
    schedule(() => { run().catch((err) => console.warn("[pathway] roll-up refresh failed", err)); });
  }

  onDisconnected() {
    this._observer?.disconnect();
    this._observer = null;
  }

  _observePages() {
    this._observer?.disconnect();
    const root = /** @type {HTMLElement | null} */ (this.shadowRoot?.querySelector(".pages"));
    if (!root) return;
    this._observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const idx = Number(/** @type {HTMLElement} */ (entry.target).dataset.pageIndex);
        if (Number.isFinite(idx)) this._setActivePage(idx);
      }
    }, { root, threshold: 0.6 });
    for (const sq of root.querySelectorAll("kfe-squiggle")) this._observer.observe(sq);
  }

  /** @param {number} idx */
  _setActivePage(idx) {
    if (idx === this._activePage) return;
    this._activePage = idx;
    const dots = this.shadowRoot?.querySelectorAll("nav.page-dots span");
    if (!dots) return;
    for (let i = 0; i < dots.length; i++) {
      dots[i].classList.toggle("active", i === idx);
    }
  }

  /** @param {Event} e */
  _onSelect = (e) => {
    const ce = /** @type {CustomEvent<{itemId: string|number}>} */ (e);
    if (!ce.detail) return;
    const id = String(ce.detail.itemId);
    if (this._level === "stage") {
      navigate("/stage/" + id);
    } else {
      navigate("/lesson/" + id);
    }
  };

  /**
   * One stage-intro row, drawn as iOS's KFEGuideView draws it (Brief 198
   * item 6). Layout + every constant: `KFEGuideView.xib`; the play counter:
   * `KFEGuideView.m:62-77 updateRequiredPlayCount:andCompletedCount:`; the
   * pulse: `StagesViewController.m:225-232`.
   *
   * Paints from the pathway tree alone (title + chevron) so it costs no fetch
   * on first paint; the icon, subtitle and counter arrive with
   * `_scheduleIntroDetailRefresh`.
   *
   * @param {IntroRow} intro
   */
  _renderIntro(intro) {
    const lang = getLanguage();
    const zh = lang === "zh-Hans";
    const title = (zh && intro.titleZh) ? intro.titleZh : (intro.titleEn || String(intro.id));
    const subtitle = (zh && intro.descZh) ? intro.descZh : (intro.descEn ?? "");
    const isLocked = this._introLocked.has(String(intro.id));
    const required = intro.requiredPlay ?? 0;
    const done = intro.completedCount ?? 0;
    // KFEGuideView.m:63 — requiredPlay < 1 pushes the counter off the row.
    const showPlays = required >= 1;
    const playsDone = showPlays && done >= required;

    const li = document.createElement("li");
    li.className = "guide"
      + (isLocked ? " locked" : "")
      // The pulse runs while the intro is unplayed, exactly as iOS gates it.
      + (showPlays && !playsDone ? " pulsing" : "");
    li.setAttribute("role", "button");
    li.setAttribute("tabindex", isLocked ? "-1" : "0");
    li.setAttribute("aria-disabled", isLocked ? "true" : "false");
    const playsLabel = !showPlays
      ? ""
      : playsDone
        ? ` — ${t("pathway.playCountDone")}`
        : ` — ${t("pathway.playCountLabel", { done, required })}`;
    li.setAttribute("aria-label", subtitle ? `${title}. ${subtitle}${playsLabel}` : `${title}${playsLabel}`);

    li.appendChild(guideIcon(intro.iconKind ?? "html"));

    const text = document.createElement("span");
    text.className = "guide-text";
    const headline = document.createElement("span");
    headline.className = "guide-headline";
    headline.textContent = title;
    text.appendChild(headline);
    if (subtitle) {
      const sub = document.createElement("span");
      sub.className = "guide-subhead";
      sub.textContent = subtitle;
      text.appendChild(sub);
    }
    li.appendChild(text);

    if (showPlays) {
      const plays = document.createElement("span");
      plays.className = "guide-plays";
      plays.innerHTML = playsDone ? WHITE_TICK_SVG : CIRCLE_ARR_SVG;
      if (!playsDone) {
        // KFEGuideView.m:74 — "%d/%d"; a completed row shows the tick alone.
        const count = document.createElement("span");
        count.className = "guide-count";
        count.textContent = `${done}/${required}`;
        plays.appendChild(count);
      }
      li.appendChild(plays);
    }

    const chevron = document.createElement("span");
    chevron.className = "guide-chevron";
    chevron.setAttribute("aria-hidden", "true");
    chevron.textContent = "\u203A";
    li.appendChild(chevron);

    if (!isLocked) {
      const open = () => navigate("/lesson/" + String(intro.id));
      li.addEventListener("click", open);
      li.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      });
    }
    return li;
  }

  /**
   * Fill in each intro row's icon, subtitle and play counter after first paint.
   *
   * iOS reads these straight off `self.introModel` plus one `app_item_info`
   * row (`StagesViewController.m:344-372`). The web's equivalents live inside
   * the intro LESSON's single item, which is lazily loaded — so this runs on
   * idle, off the first-paint path, the same way the Brief 163 roll-up
   * refresh does. Best-effort throughout: a lesson that will not load simply
   * leaves the row as the title-and-chevron version it already painted.
   *
   * @param {string} pathwayId
   */
  _scheduleIntroDetailRefresh(pathwayId) {
    if (this._intros.length === 0) return;
    const learner = /** @type {any} */ (services.learner);
    const run = async () => {
      let changed = false;
      for (const intro of this._intros) {
        if (!this._connected) return;
        try {
          const lesson = /** @type {{items?: any[]}} */ (
            await loadLesson(pathwayId, String(intro.id))
          );
          const item = (lesson?.items ?? []).find((it) => it && it.isIntro === true)
            ?? (lesson?.items ?? [])[0];
          if (!item) continue;
          intro.descEn = item.descEn;
          intro.descZh = item.descZh;
          intro.iconKind = guideIconKind(item);
          let progressRequired;
          let done = 0;
          try {
            const prog = await learner.getItemProgress(item.id);
            done = prog?.completedCount ?? 0;
            progressRequired = prog?.requiredPlay;
          } catch { /* fresh learner / stubbed store — 0 plays */ }
          // iOS passes app_item_info.completed here, which is a 0/1 flag; the
          // xib's own placeholder is "7/10", so plays-over-required is the
          // intended reading and the two agree wherever requiredPlay is 1 (it
          // is, for all 11 Zero-to-Fluency intros). See docs/198-handover.md.
          intro.requiredPlay = resolveRequiredPlay(item, progressRequired);
          intro.completedCount = done;
          changed = true;
        } catch (e) {
          console.warn("[pathway] intro detail load failed", intro.id, e);
        }
      }
      if (changed && this._connected) this._scheduleRender();
    };
    const schedule = (typeof requestIdleCallback === "function")
      ? requestIdleCallback
      : /** @param {() => void} cb */ (cb) => setTimeout(cb, 0);
    schedule(() => { run().catch((err) => console.warn("[pathway] intro refresh failed", err)); });
  }

  render() {
    // First render runs synchronously on connect, before onConnected has
    // loaded the pathway. Show "Loading…" rather than the empty/diagnostic
    // message — the latter looks like an error during normal boot (Brief 122).
    if (!this._loaded) {
      return html`<div class="empty">${t("loading")}</div>`;
    }
    // Brief 168 dispatch fork: flat (menu) pathway → delegate the whole screen
    // to the TableListViewController mirror. <kfe-pathway-list> owns all flat-list
    // logic (rows, drilldown, completion); kfe-pathway is just the router host.
    if (this._flatPathwayId) {
      const list = document.createElement("kfe-pathway-list");
      /** @type {any} */ (list).pathwayId = this._flatPathwayId;
      return html`${list}`;
    }
    if (this._pages.length === 0 && this._intros.length === 0) {
      return html`<div class="empty">${t("pathway.empty")}</div>`;
    }
    const level = this._level;
    const gatingEnabled = this._gatingEnabled;
    return html`
      ${this._intros.length > 0
        ? html`<ul class="intros" aria-label=${t("pathway.introListLabel")}>
            ${this._intros.map((intro) => this._renderIntro(intro))}
          </ul>`
        : ""}
      <div class="pages" on:select=${this._onSelect}>
        ${this._pages.map((page, i) => {
          const sq = document.createElement("kfe-squiggle");
          /** @type {any} */ (sq).page = page;
          /** @type {any} */ (sq).level = level;
          /** @type {any} */ (sq).gatingEnabled = gatingEnabled;
          sq.dataset.pageIndex = String(i);
          return sq;
        })}
      </div>
      ${this._pages.length > 0
        ? html`<p class="hint">${level === "lesson" ? t("pathway.tapLessonHint") : t("pathway.tapStageHint")}</p>`
        : ""}
      ${this._pages.length > 0
        ? html`<nav class="page-dots" aria-label=${t("pathway.pagesLabel")}>${this._pages.map((_, i) => {
            const dot = document.createElement("span");
            if (i === this._activePage) dot.classList.add("active");
            return dot;
          })}</nav>`
        : ""}
    `;
  }
}

/**
 * @param {import("../data/pathway-loader.js").PathwayStage} s
 * @returns {import("../data/pathway-builder.js").OrderedPathwayNode}
 */
function stageEntryToNode(s) {
  return {
    itemId: s.id,
    title: s.titleEn ?? "",
    titleZh: s.titleZh,
    isGate: s.type === "gate",
    // Brief 150: thread the content exemption flag so the stage squiggle's
    // gating honours self-assessment stages (e.g. stage_143095). Runtime
    // fallback (the static id set) is applied inside pathwayFromOrderedNodes.
    excludeFromGating: /** @type {any} */ (s).excludeFromGating === true,
    gateRule: /** @type {any} */ (s.gateRule),
  };
}

/**
 * @param {import("../data/pathway-loader.js").PathwayLessonRef} l
 * @returns {import("../data/pathway-builder.js").OrderedPathwayNode}
 */
function lessonEntryToNode(l) {
  return {
    itemId: l.id,
    title: l.titleEn ?? "",
    titleZh: l.titleZh,
    isGate: l.type === "gate",
    isIntroItem: l.isIntro === true,
    // Brief 150: honour an exemption flag at lesson level too (defensive — the
    // ZTF exemption is on a stage id, but a CMS could flag a lesson later).
    excludeFromGating: /** @type {any} */ (l).excludeFromGating === true,
    gateRule: /** @type {any} */ (l.gateRule),
  };
}

// ── KFEGuideView artwork ────────────────────────────────────────────────────
//
// iOS ships these as PNGs in Assets.xcassets/Icons (Video, Audio, HTML,
// Audio+HTML, CircleArr, WhiteTick). They are redrawn here as inline SVG so
// the web ships no binaries and the art stays crisp at any density — the only
// deliberate departure from iOS in this row, and a representational one.
// Only `html` is reachable with today's corpus: all 11 Zero-to-Fluency intro
// items are `reference_note`. The other three mirror iOS's own switch
// (`StagesViewController.m:351-363`) so new content lands correctly.

/** The grey disc every guide icon sits on (iOS draws it at 26 pt). */
const GUIDE_DISC = '<circle cx="13" cy="13" r="13" fill="#a6a6a6"/>';

/** A speaker + two sound arcs, as in Audio.imageset / Audio+HTML.imageset. */
const GUIDE_SPEAKER = '<path d="M6 10.5h3l3.5-3v11l-3.5-3H6z" fill="#333"/>'
  + '<path d="M15 9.5a5 5 0 0 1 0 7M17.3 7.5a8 8 0 0 1 0 11" stroke="#333" stroke-width="1.6" fill="none" stroke-linecap="round"/>';

/** @type {Record<string, string>} */
const GUIDE_ICON_SVG = {
  html: GUIDE_DISC
    + '<text x="13" y="16" text-anchor="middle" font-size="6.5" font-weight="700"'
    + ' letter-spacing="0.4" fill="#333" font-family="var(--kfe-font)">HTML</text>',
  audio: GUIDE_DISC + GUIDE_SPEAKER,
  audioHtml: GUIDE_DISC
    + '<g transform="translate(0,-2.5) scale(0.92) translate(1.1,0)">' + GUIDE_SPEAKER + '</g>'
    + '<text x="13" y="22.5" text-anchor="middle" font-size="5.5" font-weight="700"'
    + ' letter-spacing="0.3" fill="#333" font-family="var(--kfe-font)">HTML</text>',
  video: GUIDE_DISC
    + '<path d="M6.5 9.5h9a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1z" fill="#333"/>'
    + '<path d="M17.5 12l3-2.2v6.4l-3-2.2z" fill="#333"/>',
};

/**
 * iOS picks the icon off the intro item's media extension
 * (`StagesViewController.m:351-363`): .mp4 → Video, .m4a → Audio, .html →
 * HTML, or Audio+HTML when the html page has a companion .m4a. The web's
 * content items carry a `type` instead of a filename, so map through that and
 * fall back to the extension when one is present.
 *
 * @param {any} item
 * @returns {"html"|"audio"|"video"|"audioHtml"}
 */
export function guideIconKind(item) {
  const media = String(item?.media ?? "").toLowerCase();
  if (media.endsWith(".mp4")) return "video";
  if (media.endsWith(".m4a")) return "audio";
  if (media.endsWith(".html")) {
    return String(item?.audio ?? "").toLowerCase().endsWith(".m4a") ? "audioHtml" : "html";
  }
  switch (item?.type) {
    case "video_lesson": return "video";
    case "audio_video": return "audio";
    case "reading_lesson": return "audioHtml"; // iOS "Audio + HTML"
    default: return item?.companionAudioId ? "audioHtml" : "html";
  }
}

/** @param {"html"|"audio"|"video"|"audioHtml"} kind */
function guideIcon(kind) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "guide-icon");
  svg.setAttribute("viewBox", "0 0 26 26");
  svg.setAttribute("aria-hidden", "true");
  svg.innerHTML = GUIDE_ICON_SVG[kind] ?? GUIDE_ICON_SVG.html;
  return svg;
}

/**
 * CircleArr — a white ring open at the top with a right-pointing arrowhead on
 * the gap, 31 × 35 (Assets.xcassets/Icons/CircleArr, drawn at 62 × 70 at 2x).
 * The play count is overlaid on it by CSS, as the xib does.
 */
const CIRCLE_ARR_SVG = '<svg viewBox="0 0 31 35" aria-hidden="true">'
  // A 12.5 r ring with a 40 gap at the top, swept clockwise so the stroke ends
  // where the head goes, and a solid right-pointing head on that end.
  + '<path d="M25.08 9.46A12.5 12.5 0 1 1 17.67 5.19" fill="none" stroke="#fff"'
  + ' stroke-width="2" stroke-linecap="round"/>'
  + '<path d="M16.2 0.7v8l7-4z" fill="#fff"/>'
  + '</svg>';

/** WhiteTick — shown in place of the ring once the intro is played out. */
const WHITE_TICK_SVG = '<svg viewBox="0 0 31 35" aria-hidden="true">'
  + '<path d="M6.5 17.5l6 6 12-12.5" fill="none" stroke="#fff" stroke-width="3"'
  + ' stroke-linecap="round" stroke-linejoin="round"/>'
  + '</svg>';
